import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { IconDots, IconExternal, IconPlus, IconRocket } from '../components/icons'
import { Menu, type MenuAction } from '../components/overlay'
import StatusBadge, { STRIPE, toneFor } from '../components/StatusBadge'
import { CardSkeleton, ConfirmDialog, EmptyState } from '../components/ui'
import { api } from '../lib/api'
import { useLiveRefresh } from '../lib/events'
import { relativeTime } from '../lib/format'
import { useToast } from '../lib/toast'
import type { Service } from '../lib/types'
import { useApi } from '../lib/useApi'

const TYPE_LABEL: Record<Service['type'], string> = {
  web: 'Web service',
  function: 'Function',
  worker: 'Worker',
  cron: 'Cron job',
  static: 'Static site',
}

export default function Services() {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('')
  // The list moves when the control plane says it moved. The slow poll is only
  // there for the case where the event stream is down.
  const { data, error, loading, reload } = useApi(() => api.services(), [], 60_000)
  useLiveRefresh(reload, (event) => event.kind !== 'database')

  // Deleting is confirmed at this level so the dialog outlives the menu that
  // asked for it — a confirm that unmounts with its trigger never appears.
  const [pending, setPending] = useState<Service | null>(null)
  const [busy, setBusy] = useState(false)
  const { attempt } = useToast()

  const live = data?.filter((service) => service.status === 'live').length ?? 0
  const attention =
    data?.filter((service) => ['failed', 'suspended'].includes(service.status)).length ?? 0

  const destroy = async () => {
    if (!pending) return
    setBusy(true)
    await attempt(() => api.deleteService(pending.id), {
      success: `${pending.name} deleted`,
      failure: 'Could not delete the service',
    })
    setBusy(false)
    setPending(null)
    reload()
  }

  const visible = data?.filter(item => `${item.name} ${item.status}`.toLowerCase().includes(query.toLowerCase()) && (!filter || item.status === filter))
  return (
    <>
      <header className="page-head">
        <div className="titles">
          <h1>Components</h1>
          <p className="subtitle">
            {data
              ? data.length === 0
                ? 'Nothing deployed yet.'
                : `${live} live of ${data.length}${
                    attention ? ` · ${attention} needing attention` : ''
                  }`
              : 'Everything you have running in Addis Ababa.'}
          </p>
        </div>
        <Link className="btn primary" to="/services/new">
          <IconPlus className="" size={16} />
          New application
        </Link>
      </header>

      {error && <div className="alert error">{error}</div>}
      {data && data.length > 0 && <div className="resource-toolbar">
        <input type="search" aria-label="Search components" placeholder="Search by name or status…" value={query} onChange={event => setQuery(event.target.value)} />
        <select aria-label="Filter by status" value={filter} onChange={event => setFilter(event.target.value)}><option value="">All statuses</option>{[...new Set(data.map(item => item.status))].map(status => <option key={status} value={status}>{status}</option>)}</select>
        <span className="resource-count">{visible?.length} of {data.length} components</span>
      </div>}
      {data && data.length > 0 && visible?.length === 0 && <EmptyState title="No matches"><span>Try another name or clear the status filter.</span></EmptyState>}


      {loading && !data && (
        <div className="grid">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      )}

      {data?.length === 0 && (
        <EmptyState
          icon={<IconRocket className="empty-art" size={46} />}
          title="Deploy your first service"
          action={
            <Link className="btn primary" to="/services/new">
              <IconPlus className="" size={16} />
              New application
            </Link>
          }
        >
          Connect a GitHub repository and TalariCloud works out how to build it — no
          Dockerfile, no YAML. Your first service is free.
        </EmptyState>
      )}

      {data && data.length > 0 && (
        <div className="grid">
          {visible?.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              onChange={reload}
              onDelete={() => setPending(service)}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={pending !== null}
        destructive
        busy={busy}
        title={`Delete ${pending?.name}?`}
        requireText={pending?.name}
        confirmLabel="Delete service"
        body="Every instance stops and the hostname is released. Deploy history is kept for audit."
        onConfirm={destroy}
        onCancel={() => setPending(null)}
      />
    </>
  )
}

function ServiceCard({
  service,
  onChange,
  onDelete,
}: {
  service: Service
  onChange: () => void
  onDelete: () => void
}) {
  const navigate = useNavigate()
  const { attempt } = useToast()

  const run = (label: string, fn: () => Promise<unknown>, success: string) => async () => {
    await attempt(fn, { success, failure: `Could not ${label}` })
    onChange()
  }

  const actions: MenuAction[] = [
    {
      label: 'Deploy latest commit',
      onSelect: async () => {
        await attempt(
          async () => {
            const deployment = await api.deploy(service.id)
            navigate(`/deploys/${deployment.id}`)
          },
          { success: `Deploying ${service.name}`, failure: 'Could not deploy' },
        )
      },
    },
    {
      label: 'Restart',
      disabled: service.status !== 'live',
      onSelect: run('restart', () => api.restart(service.id), `${service.name} is restarting`),
    },
    service.status === 'suspended'
      ? {
          label: 'Resume',
          onSelect: run('resume', () => api.resume(service.id), `${service.name} is starting`),
        }
      : {
          label: 'Suspend',
          onSelect: run('suspend', () => api.suspend(service.id), `${service.name} is suspended`),
        },
    { label: 'Delete service', destructive: true, onSelect: onDelete },
  ]

  return (
    <div
      className="card card-link"
      style={{ '--stripe': STRIPE[toneFor(service.status)] } as React.CSSProperties}
    >
      <div className="card-head">
        {/* The whole card is the link, via the stretched pseudo-element below;
            the menu button sits above it so it stays clickable. */}
        <h2 className="grow">
          <Link className="stretched" to={`/services/${service.id}`}>
            {service.name}
          </Link>
        </h2>
        <StatusBadge status={service.status} />
        <Menu
          label={`Actions for ${service.name}`}
          actions={actions}
          trigger={
            <button type="button" className="btn ghost icon card-menu">
              <IconDots className="" size={16} />
            </button>
          }
        />
      </div>

      <p className="tiny muted truncate mono">
        {service.repo_full_name} · {service.branch}
      </p>

      {service.url && (
        <p
          className="tiny mono truncate row"
          style={{ marginTop: 10, gap: 5, color: 'var(--brand-text)' }}
        >
          <IconExternal className="" size={12} />
          {service.url.replace('https://', '')}
        </p>
      )}

      <p className="tiny faint" style={{ marginTop: 12 }}>
        {TYPE_LABEL[service.type]} · {service.plan} ·{' '}
        {service.last_deployed_at
          ? `deployed ${relativeTime(service.last_deployed_at)}`
          : 'never deployed'}
      </p>
    </div>
  )
}

